import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const GOALS = ["Weight Loss", "Fat Loss", "Strength", "Muscle Gain", "General Fitness", "Running", "HYROX Preparation", "Mobility", "Senior Fitness", "Pregnancy Fitness", "Postpartum Fitness"];

function Sel({ label, value, onChange, opts, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-2 h-12"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function Num({ label, k, form, set, ph }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</Label>
      <Input type="number" data-testid={`ob-${k}`} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value === "" ? undefined : Number(e.target.value))} className="mt-2 h-12" placeholder={ph} />
    </div>
  );
}

export default function Onboarding() {
  const { user, updateProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (user === false) return <Navigate to="/login" replace />;
  if (user && user.onboarding_complete) return <Navigate to="/app" replace />;

  const steps = [
    { title: "About you", body: (
      <div className="grid sm:grid-cols-2 gap-5">
        <Num label="Age" k="age" form={form} set={set} ph="e.g. 28" />
        <Sel label="Gender" value={form.gender} onChange={(v) => set("gender", v)} opts={["Male", "Female", "Other"]} testid="ob-gender" />
        <Num label="Height (cm)" k="height_cm" form={form} set={set} ph="e.g. 172" />
        <Num label="Current weight (kg)" k="weight_kg" form={form} set={set} ph="e.g. 78" />
        <Num label="Target weight (kg)" k="target_weight_kg" form={form} set={set} ph="e.g. 70" />
        <Num label="Occupation" k="occupation" form={form} set={set} ph="" />
      </div>) },
    { title: "Goals & activity", body: (
      <div className="grid sm:grid-cols-2 gap-5">
        <Sel label="Primary goal" value={form.goal} onChange={(v) => set("goal", v)} opts={GOALS} testid="ob-goal" />
        <Sel label="Activity level" value={form.activity_level} onChange={(v) => set("activity_level", v)} opts={["Sedentary", "Lightly active", "Moderately active", "Very active"]} testid="ob-activity" />
        <Sel label="Experience" value={form.experience} onChange={(v) => set("experience", v)} opts={["Beginner", "Intermediate", "Advanced"]} testid="ob-experience" />
        <Sel label="Where will you train?" value={form.workout_location} onChange={(v) => set("workout_location", v)} opts={["Gym Access", "Home Workout", "Both"]} testid="ob-location" />
      </div>) },
    { title: "Lifestyle", body: (
      <div className="grid sm:grid-cols-2 gap-5">
        <Sel label="Food preference" value={form.food_preference} onChange={(v) => set("food_preference", v)} opts={["Vegetarian", "Eggetarian", "Non-Vegetarian", "Vegan"]} testid="ob-food" />
        <Sel label="Equipment available" value={form.equipment} onChange={(v) => set("equipment", v)} opts={["None", "Dumbbells", "Resistance Bands", "Full Gym"]} testid="ob-equipment" />
        <Num label="Sleep (hours/night)" k="sleep_hours" form={form} set={set} ph="e.g. 7" />
        <Num label="Water intake (L/day)" k="water_intake_l" form={form} set={set} ph="e.g. 2.5" />
        <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Medical conditions (optional)</Label><Input data-testid="ob-medical" value={form.medical_conditions ?? ""} onChange={(e) => set("medical_conditions", e.target.value)} className="mt-2 h-12" placeholder="Any injuries or conditions we should know" /></div>
      </div>) },
  ];

  const finish = async () => {
    setBusy(true);
    try { await updateProfile(form); toast.success("You're all set!"); navigate("/app"); }
    catch { toast.error("Could not save. Try again."); }
    finally { setBusy(false); }
  };

  const last = step === steps.length - 1;
  return (
    <div data-testid="onboarding-page" className="min-h-screen pt-20 pb-16 bg-background">
      <div className="mx-auto max-w-2xl px-5">
        <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Step {step + 1} of {steps.length}</p>
        <div className="h-1.5 bg-secondary rounded-full overflow-hidden my-4"><motion.div className="h-full bg-primary" animate={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
            <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight mb-8">{steps[step].title}</h1>
            {steps[step].body}
          </motion.div>
        </AnimatePresence>
        <div className="flex items-center justify-between mt-10">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary disabled:opacity-30 transition-colors"><ArrowLeft size={14} /> Back</button>
          {last ? (
            <button data-testid="ob-finish" onClick={finish} disabled={busy} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60">{busy ? "Saving…" : <>Enter Dashboard <ArrowRight size={16} /></>}</button>
          ) : (
            <button data-testid="ob-next" onClick={() => setStep((s) => s + 1)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform">Next <ArrowRight size={16} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
